import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CalendarDays,
  ContactRound,
  Home,
  LayoutGrid,
  LifeBuoy,
  Menu,
  Plug,
  Server,
  Sparkles,
  Zap,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { allowedPaths } from '@/lib/auth';
import { useWorkspaceFocus } from '@/hooks/use-hosting-only';
import { ADD_FEATURES_PATH, openInFocus } from '@/lib/workspace-modules';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Sidebar } from './sidebar';

// 4 quick-access tabs + a "More" button that opens the full sidebar nav in a
// drawer. Visible only on mobile (< md). The Sidebar component is reused
// inside the drawer so we don't duplicate the nav tree.
// Candidate quick-tabs, in priority order. We show the first 4 the current
// role is allowed to open, so every role gets sensible bottom-nav tabs.
const TAB_POOL = [
  { to: '/dashboard',            label: 'Home',        icon: Home },
  // A store's phone tabs are the store's pages. They ride the /commerce
  // permission, so a lead-gen workspace never sees them and falls through
  // to Pipeline / Contacts below.
  { to: '/commerce',             label: 'Orders',      icon: LayoutGrid, gate: '/commerce/orders' },
  { to: '/commerce/customers',   label: 'Customers',   icon: ContactRound, gate: '/commerce/customers' },
  { to: '/commerce/recovery',    label: 'Recovery',    icon: Zap, gate: '/commerce/recovery' },
  { to: '/leads',                label: 'Pipeline',    icon: LayoutGrid },
  { to: '/contacts',             label: 'Contacts',    icon: ContactRound },
  { to: '/calendar',             label: 'Calendar',    icon: CalendarDays },
  { to: '/tracking/attribution', label: 'Attribution', icon: Zap },
  { to: '/integrations',         label: 'Integrations', icon: Plug, gate: ['/settings', '/tracking/destinations'] },
  { to: '/tracking/health',      label: 'Health',      icon: LifeBuoy },
];

// A focused workspace (lib/workspace-modules) leads with its own two pages,
// then whatever its switched-on cards opened.
const FOCUS_TABS = [
  { to: '/server-container', label: 'Server GTM', icon: Server },
  { to: ADD_FEATURES_PATH, label: 'Add', icon: Sparkles, gate: '/server-container' },
];

export function MobileBottomNav({ hidden = false }) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const allow = allowedPaths();
  const { focus, modules } = useWorkspaceFocus();
  const pool = focus ? [...FOCUS_TABS, ...TAB_POOL.filter((t) => openInFocus(t.to, modules))] : TAB_POOL;
  const tabs = pool.filter((t) => [].concat(t.gate || t.to).some((g) => allow.has(g))).slice(0, 4);

  function isActive(to) {
    if (to === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(to);
  }

  return (
    <>
      <nav
        className={cn(
          'fixed bottom-0 left-0 right-0 z-40 flex h-14 items-stretch border-t border-border bg-background/95 backdrop-blur transition-transform duration-200 md:hidden',
          hidden && 'translate-y-full',
        )}
        aria-label="Primary mobile navigation"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.to);
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={cn(
                'flex flex-1 flex-col items-center justify-center gap-0.5 text-[12px] font-medium transition-colors',
                active ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon
                className={cn('size-5', active && 'text-primary')}
                strokeWidth={active ? 2.25 : 1.75}
              />
              <span>{tab.label}</span>
            </Link>
          );
        })}

        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex flex-1 flex-col items-center justify-center gap-0.5 text-[12px] font-medium text-muted-foreground hover:text-foreground"
          aria-label="More navigation"
        >
          <Menu className="size-5" strokeWidth={1.75} />
          <span>More</span>
        </button>
      </nav>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-[260px] p-0 pt-2">
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation</SheetTitle>
          </SheetHeader>
          {/* Reuse the desktop sidebar but force the expanded width inside
              the drawer. The drawer itself handles open/close. Tapping any
              link closes the drawer because the route changes. */}
          <div
            className="h-[calc(100vh-1rem)] overflow-y-auto"
            onClick={(e) => {
              // Auto-close drawer when a nav link is tapped
              if (e.target.closest('a[href]')) setDrawerOpen(false);
            }}
          >
            <Sidebar collapsed={false} onToggle={() => {}} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
